import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Calendar } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckIn({ user }) {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [classInfo, setClassInfo] = useState(null);
  const [classOptions, setClassOptions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    attemptCheckin();
  }, []);

  const verifyCheckinStatus = async () => {
    try {
      const res = await axios.get(`${API}/checkin/today-classes`);
      const checkedIn = res.data.find(c => c.checked_in);
      if (checkedIn) {
        setClassInfo({ title: checkedIn.title, time: `${checkedIn.start_time} - ${checkedIn.end_time}` });
        setStatus('already');
        setMessage(`You're checked in to '${checkedIn.title}'!`);
        return true;
      }
    } catch {}
    return false;
  };

  const attemptCheckin = async () => {
    try {
      const response = await axios.post(`${API}/checkin/auto`, {});
      const data = response.data;
      if (data.needs_selection) {
        setClassOptions(data.classes);
        setStatus('pick');
      } else if (data.already_checked_in) {
        setClassInfo({ title: data.class_title, time: data.class_time });
        setStatus('already');
        setMessage(data.message);
      } else {
        setClassInfo({ title: data.class_title, time: data.class_time });
        setStatus('success');
        setMessage(data.message);
        toast.success(data.message);
      }
    } catch (error) {
      // Before showing error, verify if check-in actually succeeded
      const wasCheckedIn = await verifyCheckinStatus();
      if (!wasCheckedIn) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Check-in failed. Please try again.');
      }
    }
  };

  const checkinToClass = async (classId) => {
    setStatus('loading');
    try {
      const response = await axios.post(`${API}/checkin/auto`, { class_id: classId });
      const data = response.data;
      setClassInfo({ title: data.class_title, time: data.class_time });
      if (data.already_checked_in) {
        setStatus('already');
        setMessage(data.message);
      } else {
        setStatus('success');
        setMessage(data.message);
        toast.success(data.message);
      }
    } catch (error) {
      const wasCheckedIn = await verifyCheckinStatus();
      if (!wasCheckedIn) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Check-in failed.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="checkin-page">
      <Card className="bg-[#18181B] border-[#27272A] p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <div className="space-y-4" data-testid="checkin-loading">
            <Loader2 className="w-16 h-16 text-[#CCFF00] animate-spin mx-auto" />
            <p className="text-[#FAFAFA] text-xl font-heading uppercase">Checking you in...</p>
          </div>
        )}

        {status === 'pick' && (
          <div className="space-y-4" data-testid="checkin-pick">
            <div className="bg-[#CCFF00]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
              <Calendar className="w-10 h-10 text-[#CCFF00]" />
            </div>
            <h2 className="text-2xl font-heading font-black uppercase text-[#FAFAFA]">Which Class?</h2>
            <p className="text-[#A1A1AA] text-sm">You're booked into multiple classes. Select which one you're attending:</p>
            <div className="space-y-3 mt-4">
              {classOptions.map((cls) => (
                <Button
                  key={cls.class_id}
                  onClick={() => checkinToClass(cls.class_id)}
                  data-testid={`pick-class-${cls.class_id}`}
                  className="w-full bg-[#09090B] border border-[#27272A] hover:border-[#CCFF00] text-left p-4 h-auto flex flex-col items-start rounded-sm"
                >
                  <span className="text-lg font-heading font-bold uppercase text-[#FAFAFA]">{cls.title}</span>
                  <span className="text-sm text-[#A1A1AA]">{cls.start_time} - {cls.end_time}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4" data-testid="checkin-success">
            <div className="bg-[#22C55E]/10 w-24 h-24 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-14 h-14 text-[#22C55E]" />
            </div>
            <h2 className="text-3xl font-heading font-black uppercase text-[#FAFAFA]">Checked In!</h2>
            {classInfo && (
              <div className="bg-[#09090B] border border-[#27272A] rounded-sm p-4">
                <p className="text-lg font-heading font-bold uppercase text-[#CCFF00]">{classInfo.title}</p>
                <p className="text-sm text-[#A1A1AA]">{classInfo.time}</p>
              </div>
            )}
            <p className="text-[#A1A1AA]">{message}</p>
          </div>
        )}

        {status === 'already' && (
          <div className="space-y-4" data-testid="checkin-already">
            <div className="bg-[#CCFF00]/10 w-24 h-24 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-14 h-14 text-[#CCFF00]" />
            </div>
            <h2 className="text-3xl font-heading font-black uppercase text-[#FAFAFA]">Already Here!</h2>
            {classInfo && (
              <div className="bg-[#09090B] border border-[#27272A] rounded-sm p-4">
                <p className="text-lg font-heading font-bold uppercase text-[#CCFF00]">{classInfo.title}</p>
                <p className="text-sm text-[#A1A1AA]">{classInfo.time}</p>
              </div>
            )}
            <p className="text-[#A1A1AA]">{message}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4" data-testid="checkin-error">
            <div className="bg-[#EF4444]/10 w-24 h-24 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-14 h-14 text-[#EF4444]" />
            </div>
            <h2 className="text-3xl font-heading font-black uppercase text-[#FAFAFA]">Check-in Failed</h2>
            <p className="text-[#A1A1AA]">{message}</p>
            <Button
              onClick={() => { setStatus('loading'); attemptCheckin(); }}
              data-testid="checkin-retry"
              variant="outline"
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]"
            >
              Try Again
            </Button>
          </div>
        )}

        <Button
          onClick={() => navigate('/whiteboard')}
          data-testid="checkin-go-home"
          className="mt-6 w-full bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm"
        >
          Go to Whiteboard
        </Button>
      </Card>
    </div>
  );
}
